import React from 'react';
import { View, TouchableOpacity, StyleSheet, Image } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { Job } from '../../types/work';
import { getRelativeTime } from '../../data/mockWorkData';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface ProductCardProps {
  product: Job;
  onPress: () => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, onPress }) => {
  const { theme } = useTheme();
  
  return (
    <TouchableOpacity 
      style={[styles.cardWrapper, { backgroundColor: theme.card, borderColor: theme.border }]} 
      onPress={onPress} 
      activeOpacity={0.7}
    >
      {/* Product Image - Left Side */}
      {product.images && product.images.length > 0 ? (
        <Image 
          source={{ uri: product.images[0] }} 
          style={styles.productImage}
          resizeMode="cover"
        />
      ) : (
        <View style={[styles.placeholderImage, { backgroundColor: theme.backgroundSecondary }]}>
          <Ionicons name="image-outline" size={40} color={theme.textTertiary} />
        </View>
      )}
      
      {/* Product Info - Right Side */}
      <View style={styles.cardContent}>
        {/* Title */}
        <ThemedText style={[styles.title, { color: theme.text }]} numberOfLines={2}>
          {product.title}
        </ThemedText>

        {/* Category */}
        <View style={[styles.categoryBadge, { backgroundColor: theme.backgroundSecondary }]}>
          <Ionicons name="pricetag-outline" size={12} color={theme.textSecondary} />
          <ThemedText style={[styles.categoryText, { color: theme.textSecondary }]}>{product.category}</ThemedText>
        </View>

        {/* Price & Time Row */}
        <View style={styles.bottomRow}>
          <ThemedText style={[styles.priceText, { color: theme.accent }]}>
            ${product.price?.toLocaleString()}
          </ThemedText>
          <View style={styles.timeContainer}>
            <Ionicons name="time-outline" size={12} color={theme.textTertiary} />
            <ThemedText style={[styles.timeText, { color: theme.textTertiary }]}>
              {getRelativeTime(product.createdAt)}
            </ThemedText>
          </View>
        </View>
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  cardWrapper: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginBottom: 12,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
  },
  productImage: {
    width: 110,
    height: 110,
  },
  placeholderImage: {
    width: 110,
    height: 110,
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardContent: {
    flex: 1,
    padding: 12,
    justifyContent: 'space-between',
  },
  title: {
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
    marginBottom: 6,
    letterSpacing: -0.2,
  },
  categoryBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginBottom: 'auto',
  },
  categoryText: {
    fontSize: 11,
    fontWeight: '600',
    textTransform: 'capitalize',
  },
  bottomRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: 4,
  },
  priceText: {
    fontSize: 18,
    fontWeight: '800',
    letterSpacing: -0.5,
  },
  timeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  timeText: {
    fontSize: 11,
    fontWeight: '500',
  },
});

export default ProductCard;
